import * as XLSX from 'xlsx';

/* ============================================================
   Import fields — the boxes on the Add Product / Add Service forms
   that a spreadsheet column can fill.
============================================================ */

const MONEY_FIELDS = new Set(['price', 'sellingPrice', 'msrp', 'shipping', 'tariff']);
const CUSTOM_SPEC = 'customSpec';
const MULTILINE_FIELDS = new Set(['description', 'notes']);
const PRODUCT_ONLY_FIELDS = new Set([
  'sku',
  'msrp',
  'shipping',
  'tariff',
  'color',
  'finishes',
  'materials',
  'length',
  'width',
  'height',
  'depth',
  'customSpec',
]);

const FIELD_LABELS = {
  name: { product: 'Product Name', service: 'Service Name' },
  sku: 'SKU',
  category: 'Category',
  vendor: 'Vendor',
  salesCode: 'Sales Code',
  price: { product: 'Trade Price Per Unit', service: 'Trade Price' },
  sellingPrice: { product: 'Selling Price Per Unit', service: 'Selling Price' },
  markup: 'Markup % (for selling price)',
  msrp: 'MSRP',
  shipping: 'Shipping Cost',
  tariff: 'Tariff Cost',
  color: 'Color',
  finishes: 'Finishes',
  materials: 'Materials / Fabric',
  description: 'Client-Facing Description',
  notes: 'Internal Notes',
  link: { product: 'Product Link', service: 'Service Link' },
  imageFile: { product: 'Product Image Filename', service: 'Service Image Filename' },
  imageUrl: { product: 'Product Image URL', service: 'Service Image URL' },
  length: 'Length',
  width: 'Width',
  height: 'Height',
  depth: 'Depth',
  customSpec: 'Custom Spec (column name as label)',
  archived: 'Archived (left unselected)',
};

// Column headers each field is matched to automatically, compared after
// normalizeHeader(). Earlier entries win when a file has more than one candidate.
// Names come from Houzz Pro / Ivy tracker exports, Ivy's product library
// (Company Name is where it's bought, Manufacturer the brand), Workroom (Indema)
// and plain spreadsheets.
const FIELD_HEADERS = {
  name: [
    'title',
    'title original',
    'name',
    'product name',
    'service name',
    'item name',
    'product',
    'item',
    'title client facing',
  ],
  sku: [
    'sku',
    'model number',
    'model #',
    'model',
    'item number',
    'item #',
    'product code',
    'part number',
    'style number',
  ],
  category: ['category', 'category name', 'product category'],
  vendor: [
    'vendor',
    'vendor name',
    'vendor or sub',
    'company name',
    'manufacturer',
    'supplier',
    'brand',
  ],
  salesCode: ['sales code', 'sales account'],
  price: [
    'unit purchase cost',
    'purchase unit cost',
    'unit cost',
    'cost per unit',
    'trade price per unit',
    'trade price',
    'trade',
    'cost',
    'net price',
    'purchase price',
    'wholesale price',
    'wholesale',
    // Ivy's product library and service list: the cost before markup.
    'price',
    'rate',
  ],
  sellingPrice: [
    'unit selling price',
    'selling price per unit',
    'selling price',
    'sell price',
    'client price',
    'unit price',
  ],
  markup: ['markup %', 'markup', 'markup percent', 'markup percentage', 'mark up %', 'mark up'],
  msrp: ['msrp', 'retail price', 'list price', 'retail'],
  shipping: ['shipping cost', 'shipping', 'freight cost', 'freight'],
  tariff: ['tariff cost', 'tariff', 'duty'],
  color: ['color', 'colour'],
  finishes: ['finish color', 'finishes', 'finish'],
  materials: ['materials fabric', 'materials', 'material', 'fabric'],
  description: [
    'client description',
    'client facing description',
    'description',
    'product description',
    'item description',
  ],
  notes: ['internal notes', 'notes', 'note'],
  link: [
    'product link',
    'product url',
    'service link',
    'website link',
    'url',
    'link',
    'website',
    'link click box to visit site',
  ],
  length: ['length'],
  width: ['width'],
  height: ['height'],
  depth: ['depth'],
  // Free-text sizes ("24"W x 13"D") don't fit the number fields above.
  customSpec: ['dimensions', 'dimension'],
  archived: ['is archived', 'archived', 'discontinued'],
};

// Image columns hold either filenames ("ugug5_img499r.jpeg") or links; which one
// is decided from the column's contents.
const IMAGE_HEADERS = [
  'image',
  'image filename',
  'image file',
  'image link',
  'image url',
  'photo',
  'picture',
  'thumbnail',
];

const KNOWN_HEADERS = new Set([...Object.values(FIELD_HEADERS).flat(), ...IMAGE_HEADERS]);

const FIELD_ORDER = [
  'name',
  'sku',
  'category',
  'vendor',
  'salesCode',
  'price',
  'sellingPrice',
  'markup',
  'msrp',
  'shipping',
  'tariff',
  'color',
  'finishes',
  'materials',
  'description',
  'notes',
  'link',
  'imageFile',
  'imageUrl',
  'length',
  'width',
  'height',
  'depth',
  'customSpec',
  'archived',
];

export function getImportFields(itemType) {
  return FIELD_ORDER.filter((key) => itemType !== 'service' || !PRODUCT_ONLY_FIELDS.has(key)).map(
    (key) => ({ key, label: fieldLabel(key, itemType), money: MONEY_FIELDS.has(key) })
  );
}

export function fieldLabel(key, itemType) {
  const label = FIELD_LABELS[key];
  if (!label) return key;
  return typeof label === 'string' ? label : label[itemType === 'service' ? 'service' : 'product'];
}

/* ============================================================
   Reading the spreadsheet
============================================================ */

export function normalizeHeader(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9%#]+/g, ' ')
    .trim();
}

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

// An Ivy account export puts every table on one sheet, each under a banner:
//   ---------------------------------------------
//   Clients
//   ---------------------------------------------
//   Name | Company Name | Email | ...
const isBannerRow = (row) =>
  /^-{10,}$/.test(String(row?.[0] ?? '').trim()) && (row || []).slice(1).every(isBlank);

// Sections share the sheet's width, so a narrow table would otherwise drag along
// every empty column of the widest one.
function trimEmptyColumns(rows) {
  let width = 0;
  rows.forEach((row) => {
    for (let i = row.length - 1; i >= width; i -= 1) {
      if (!isBlank(row[i])) {
        width = i + 1;
        break;
      }
    }
  });
  return rows.map((row) => row.slice(0, width));
}

// Each banner section comes back as a sheet of its own, named after its banner.
export function splitSections(sheet) {
  const { rows } = sheet;
  const rowNumbers = sheet.rowNumbers || rows.map((_, i) => i + 1);
  const extras = sheet.extras || rows.map(() => null);
  const starts = [];
  for (let i = 0; i + 2 < rows.length; i += 1) {
    if (isBannerRow(rows[i]) && isBannerRow(rows[i + 2]) && !isBlank(rows[i + 1]?.[0])) {
      starts.push(i);
      i += 2;
    }
  }
  if (starts.length < 2) return [sheet];
  return starts
    .map((start, index) => {
      const end = index + 1 < starts.length ? starts[index + 1] : rows.length;
      return {
        ...sheet,
        name: String(rows[start + 1][0]).trim(),
        rows: trimEmptyColumns(rows.slice(start + 3, end)),
        rowNumbers: rowNumbers.slice(start + 3, end),
        extras: extras.slice(start + 3, end),
        section: true,
      };
    })
    .filter((section) => section.rows.length > 0);
}

const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(String(value ?? '').trim());

// What a cell holds besides its value, by sheet row: the date as Excel shows it
// for date cells, whose value stays the day count the record import reads, and
// hyperlinks (a product page on the name, a photo on an image filename).
// Read with cellNF, a date cell also carries its number format, which
// sheet_to_json would use to turn the day count into a Date, so the format is
// dropped once it's been read.
function takeCellExtras(sheet, firstCol) {
  const byRow = new Map();
  Object.keys(sheet).forEach((address) => {
    if (address[0] === '!') return;
    const cell = sheet[address];
    const date = cell.t === 'n' && cell.z && cell.w && XLSX.SSF.is_date(cell.z) ? cell.w : '';
    const link = isHttpUrl(cell.l?.Target) ? cell.l.Target.trim() : '';
    delete cell.z;
    if (!date && !link) return;
    const { r, c } = XLSX.utils.decode_cell(address);
    const entry = byRow.get(r) || {};
    if (date) entry.dates = { ...entry.dates, [c - firstCol]: date };
    if (link) entry.links = { ...entry.links, [c - firstCol]: link };
    byRow.set(r, entry);
  });
  return byRow;
}

// CSV comes in as text so accented characters survive and leading zeros in
// SKUs stay put; Excel files come in as bytes. Blank rows are dropped, but
// rowNumbers keeps each remaining row's place in the file. Dates in Excel files
// stay serial numbers; date1904 says which calendar they count from. `extras`
// lines up with the rows: each row's dates and hyperlinks, keyed by column.
export function readWorkbook(data, { csv = false } = {}) {
  const workbook = csv
    ? XLSX.read(data, { type: 'string', raw: true })
    : XLSX.read(data, { type: 'array', cellNF: true });
  const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904);

  return workbook.SheetNames.flatMap((name) => {
    const sheet = workbook.Sheets[name];
    if (!sheet?.['!ref']) return [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const extrasByRow = takeCellExtras(sheet, range.s.c);
    const rows = [];
    const rowNumbers = [];
    const extras = [];
    XLSX.utils
      .sheet_to_json(sheet, { header: 1, raw: true, defval: '', blankrows: true })
      .forEach((row, index) => {
        if (!row.some((cell) => !isBlank(cell))) return;
        rows.push(row);
        rowNumbers.push(range.s.r + 1 + index);
        extras.push(extrasByRow.get(range.s.r + index) || null);
      });
    return rows.length ? splitSections({ name, rows, rowNumbers, extras, date1904 }) : [];
  });
}

// Reports often start with a company or project title above the table, so the
// header is the early row that looks most like a list of column names.
export function detectHeaderRow(rows, knownHeaders = KNOWN_HEADERS) {
  let best = 0;
  let bestScore = -1;
  rows.slice(0, 15).forEach((row, index) => {
    const cells = (row || []).map(normalizeHeader).filter(Boolean);
    if (cells.length < 2) return;
    const known = cells.filter((cell) => knownHeaders.has(cell)).length;
    const score = known * 10 + cells.length;
    if (score > bestScore) {
      best = index;
      bestScore = score;
    }
  });
  return best;
}

// `knownHeaders` scores the header row for what's being imported. One-cell rows
// are room or group labels in tracker exports, but a client list can have a
// name and nothing else, so other imports keep them. `rowNumbers` (from
// readWorkbook) gives each row's place in the file, and `extras` its dates and
// hyperlinks, which a data row keeps as `dates` and `links` beside its cells.
export function buildSheet(
  sheetName,
  rows,
  { knownHeaders, keepSingleCellRows = false, rowNumbers, extras } = {}
) {
  const headerRowIndex = detectHeaderRow(rows, knownHeaders);
  const rowNumberAt = (index) => (rowNumbers ? rowNumbers[index] : index + 1);
  const headerCells = rows[headerRowIndex] || [];
  // Reduce, not Math.max(...spread): a file with 100k+ rows would overflow the
  // call stack when spread as arguments.
  const width = rows.reduce((max, row) => Math.max(max, (row || []).length), headerCells.length);

  const headers = Array.from({ length: width }, (_, i) => {
    const text = String(headerCells[i] ?? '').trim();
    return text || `Column ${XLSX.utils.encode_col(i)}`;
  });

  const dataRows = [];
  let skippedRows = 0;
  rows.slice(headerRowIndex + 1).forEach((row, offset) => {
    const cells = headers.map((_, i) => (row || [])[i] ?? '');
    const filled = cells.filter((cell) => !isBlank(cell)).length;
    // Blank rows and one-cell rows (a room or group label between items) aren't products.
    if (filled === 0 || (filled === 1 && !keepSingleCellRows)) {
      if (filled === 1) skippedRows += 1;
      return;
    }
    const extra = extras?.[headerRowIndex + 1 + offset];
    dataRows.push({
      rowNumber: rowNumberAt(headerRowIndex + 1 + offset),
      cells,
      ...(extra?.dates && { dates: extra.dates }),
      ...(extra?.links && { links: extra.links }),
    });
  });

  return { name: sheetName, headerRowIndex, headers, dataRows, skippedRows };
}

/* ============================================================
   Column mapping
============================================================ */

const looksLikeUrl = (value) => /^https?:\/\//i.test(String(value ?? '').trim());

export function autoMapColumns(headers, dataRows, itemType) {
  const allowed = new Set(getImportFields(itemType).map((f) => f.key));
  const normalized = headers.map(normalizeHeader);
  const mapping = headers.map(() => '');

  FIELD_ORDER.forEach((field) => {
    const candidates = FIELD_HEADERS[field];
    if (!candidates || !allowed.has(field)) return;
    if (field === CUSTOM_SPEC) {
      normalized.forEach((header, i) => {
        if (!mapping[i] && candidates.includes(header)) mapping[i] = field;
      });
      return;
    }
    for (const candidate of candidates) {
      const col = normalized.findIndex((header, i) => header === candidate && !mapping[i]);
      if (col !== -1) {
        mapping[col] = field;
        return;
      }
    }
  });

  normalized.forEach((header, col) => {
    if (mapping[col] || !IMAGE_HEADERS.includes(header)) return;
    const values = dataRows.map((row) => row.cells[col]).filter((v) => !isBlank(v));
    const urlCount = values.filter(looksLikeUrl).length;
    const field = values.length && urlCount * 2 >= values.length ? 'imageUrl' : 'imageFile';
    if (!mapping.includes(field)) mapping[col] = field;
  });

  return mapping;
}

// A field can come from one column only; choosing it for a column takes it off
// whichever column had it before. Custom specs are the exception: each column
// picked for one becomes its own spec.
export function assignColumnField(mapping, col, field) {
  return mapping.map((current, i) => {
    if (i === col) return field;
    return field && field !== CUSTOM_SPEC && current === field ? '' : current;
  });
}

// Matched columns first, in the order the fields appear on the product form,
// then the unmatched ones as they sit in the file.
const DISPLAY_ORDER = [
  'name',
  'category',
  'vendor',
  'salesCode',
  'price',
  'sellingPrice',
  'markup',
  'imageFile',
  'imageUrl',
  'description',
  'sku',
  'materials',
  'finishes',
  'color',
  'msrp',
  'shipping',
  'tariff',
  'notes',
  'link',
  'length',
  'width',
  'height',
  'depth',
  'customSpec',
  'archived',
];

export function columnDisplayOrder(mapping) {
  const columns = mapping.map((field, col) => ({ field, col }));
  const matched = columns
    .filter((c) => c.field)
    .sort((a, b) => DISPLAY_ORDER.indexOf(a.field) - DISPLAY_ORDER.indexOf(b.field));
  return [...matched, ...columns.filter((c) => !c.field)].map((c) => c.col);
}

// The first value in a column. Dates show as Excel shows them, not as the day
// count the cell holds, and a link-only cell shows its link.
export function columnSample(dataRows, col, field) {
  const found = dataRows.find((row) => !isBlank(row.cells[col]) || row.links?.[col]);
  if (!found) return '';
  const value = found.cells[col];
  if (isBlank(value)) return found.links[col];
  if (found.dates?.[col]) return found.dates[col];
  if (MONEY_FIELDS.has(field)) {
    const amount = parseMoney(value);
    if (amount !== null) return `$${amount.toFixed(2)}`;
  }
  return cellText(value).replace(/\s+/g, ' ').trim();
}

/* ============================================================
   Values
============================================================ */

export function parseMoney(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (!text) return null;
  const negative = text.startsWith('-') || /^\(.*\)$/.test(text);
  const digits = text.replace(/[^0-9.]/g, '');
  if (!digits || digits === '.') return null;
  const amount = Number(digits);
  if (!Number.isFinite(amount)) return null;
  return negative ? -amount : amount;
}

// Prices are kept as "0.00" strings for the review inputs. Zero and negative
// amounts count as missing: tracker exports put 0 in the selling price of PO
// lines and in the cost of proposal lines.
export function formatPrice(value) {
  const amount = parseMoney(value);
  return amount !== null && amount > 0 ? amount.toFixed(2) : '';
}

export function parseMarkup(value) {
  if (isBlank(value)) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const amount = Number(String(value).replace(/[%,\s]/g, ''));
  return Number.isFinite(amount) ? amount : null;
}

// Markup columns hold 35 for 35%, unless the sheet stores percentages as
// fractions (0.35), which shows up as every value being 1 or less.
function markupScale(dataRows, col) {
  if (col === -1) return 1;
  const values = dataRows.map((row) => parseMarkup(row.cells[col])).filter((v) => v);
  return values.length && values.every((v) => Math.abs(v) <= 1) ? 100 : 1;
}

/* ----- Selling price modes -----
   Tracker exports from Houzz Pro / Ivy list "Unit Selling Price" before the
   line's markup: Total Selling Price = quantity x Unit Selling Price x (1 + Markup %).
   'withMarkup' adds the markup back so the selling price is what the client was
   charged; 'file' takes the column as it is; 'none' skips selling prices.
   Ivy's product library has no selling price, only Price (the cost) and Markup:
   'tradeMarkup' works the selling price out from those two. */

// The choices the mapped columns allow, in the order they're offered.
export function sellPriceModes(mapping) {
  const has = (field) => mapping.includes(field);
  if (has('sellingPrice')) return [...(has('markup') ? ['withMarkup'] : []), 'file', 'none'];
  if (has('markup') && has('price')) return ['tradeMarkup', 'none'];
  return [];
}

export function defaultSellPriceMode(mapping) {
  const has = (field) => mapping.includes(field);
  if (has('sellingPrice')) return has('markup') ? 'withMarkup' : 'file';
  return has('markup') && has('price') ? 'tradeMarkup' : 'file';
}

// A mode the columns no longer allow (one was unmapped) falls back to the default.
export function effectiveSellPriceMode(mapping, mode) {
  const modes = sellPriceModes(mapping);
  if (!modes.length) return 'file';
  return modes.includes(mode) ? mode : defaultSellPriceMode(mapping);
}

function priceParts(row, mapping, scale, priceField = 'sellingPrice') {
  const sellCol = mapping.indexOf(priceField);
  const markupCol = mapping.indexOf('markup');
  const unit = sellCol === -1 ? null : parseMoney(row.cells[sellCol]);
  const markup = markupCol === -1 ? null : parseMarkup(row.cells[markupCol]);
  return {
    unit: unit !== null && unit > 0 ? unit : null,
    markup: markup === null ? null : markup * scale,
  };
}

// The first row with both a price and a markup, worked through, so the person
// importing can check the arithmetic against their own file.
export function sellPriceExample(dataRows, mapping, mode = 'withMarkup') {
  const scale = markupScale(dataRows, mapping.indexOf('markup'));
  const priceField = mode === 'tradeMarkup' ? 'price' : 'sellingPrice';
  for (const row of dataRows) {
    const { unit, markup } = priceParts(row, mapping, scale, priceField);
    if (unit && markup) {
      return {
        rowNumber: row.rowNumber,
        unit,
        markup,
        total: Math.round(unit * (1 + markup / 100) * 100) / 100,
      };
    }
  }
  return null;
}

// Excel keeps some numbers with float noise (70.69999999999999); show them the
// way Excel displays them.
function cellText(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Number(value.toPrecision(12)));
  }
  return String(value ?? '');
}

function cleanText(value, multiline) {
  if (isBlank(value)) return '';
  const text = cellText(value).replace(/\r\n?/g, '\n');
  if (!multiline) return text.replace(/\s+/g, ' ').trim();
  return text
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
}

const norm = (value) =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/* ============================================================
   Review rows
============================================================ */

// Markup only feeds the selling price, and images, custom specs and the archived
// flag are kept on the row itself, so none of them is one of the field boxes.
const ROW_LEVEL_FIELDS = new Set(['imageFile', 'imageUrl', 'markup', 'archived', CUSTOM_SPEC]);
export const EMPTY_FIELDS = FIELD_ORDER.filter((key) => !ROW_LEVEL_FIELDS.has(key)).reduce(
  (acc, key) => ({ ...acc, [key]: '' }),
  {}
);

const isYes = (value) => value === true || /^(true|yes|y|1|x)$/i.test(String(value ?? '').trim());

export const catalogKey = (name, vendorName) => `${norm(name)}|${norm(vendorName)}`;

export const catalogKeySet = (products) =>
  new Set(products.map((p) => catalogKey(p.name, p.vendorName || p.vendor)));

// Ivy fills in the vendor of time, fee and expense lines as "No vendor".
const NO_VENDOR = /^(no vendor|none|n\/?a|-+)$/i;

// Ivy's photo links are signed S3 links that stop working a day or so after the
// export (X-Amz-Date plus X-Amz-Expires seconds). Once one has, there's no point
// trying to copy it.
export function linkExpired(link, now = Date.now()) {
  let params;
  try {
    params = new URL(link).searchParams;
  } catch {
    return false;
  }
  const signed = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(
    params.get('X-Amz-Date') || ''
  );
  const seconds = Number(params.get('X-Amz-Expires'));
  if (!signed || !seconds) return false;
  const [, y, mo, d, h, mi, s] = signed.map(Number);
  return Date.UTC(y, mo - 1, d, h, mi, s) + seconds * 1000 < now;
}

// Selling price and the price before markup are worked out per row, before
// repeats are combined, so a price is never paired with another row's markup.
// A photo the file links to (a hyperlinked image filename, or an image URL) is
// kept as imageLink, to be copied into the studio's uploads during the import.
function readRowFields(row, mapping, { sellPriceMode, scale, headers, now }) {
  const fields = { ...EMPTY_FIELDS };
  let imageFile = '';
  let imageUrl = '';
  let imageLink = '';
  let expiredLink = false;
  let nameLink = '';
  let archived = false;
  const customSpecs = [];
  mapping.forEach((field, col) => {
    if (!field || field === 'sellingPrice' || field === 'markup') return;
    // A date cell reads as Excel shows it, not as its day count.
    const raw = row.dates?.[col] ?? row.cells[col];
    const link = row.links?.[col] || '';
    if (field === 'imageFile') {
      imageFile = cleanText(raw, false);
      imageLink = link;
    } else if (field === 'imageUrl') {
      imageUrl = looksLikeUrl(raw) ? String(raw).trim() : '';
      imageLink = imageUrl || link;
    } else if (field === 'archived') {
      archived = isYes(raw);
    } else if (field === CUSTOM_SPEC) {
      const value = cleanText(raw, false);
      if (value) customSpecs.push({ label: String(headers[col] ?? '').trim() || 'Spec', value });
    } else if (MONEY_FIELDS.has(field)) {
      fields[field] = formatPrice(row.cells[col]);
    } else if (field === 'link') {
      // A "Click here" cell's link is the link, not its text.
      fields.link = link || cleanText(raw, false);
    } else {
      if (field === 'name') nameLink = link;
      fields[field] = cleanText(raw, MULTILINE_FIELDS.has(field));
    }
  });
  if (imageLink && linkExpired(imageLink, now)) {
    expiredLink = true;
    imageLink = '';
  }
  // A product library can link each name to the product's page.
  if (!fields.link) fields.link = nameLink;
  if (NO_VENDOR.test(fields.vendor)) fields.vendor = '';
  // A number isn't a category name; Ivy's service list has 0 in every row.
  if (/^\d+(\.\d+)?$/.test(fields.category)) fields.category = '';

  const { unit, markup } = priceParts(row, mapping, scale);
  let basePrice = '';
  if (unit && sellPriceMode !== 'none') {
    const addMarkup = sellPriceMode === 'withMarkup' && markup !== null;
    fields.sellingPrice = formatPrice(addMarkup ? unit * (1 + markup / 100) : unit);
    // With a markup on top, the price before markup is what the designer paid.
    if (addMarkup && markup > 0) basePrice = formatPrice(unit);
  }
  if (sellPriceMode === 'tradeMarkup') {
    const trade = priceParts(row, mapping, scale, 'price');
    if (trade.unit && trade.markup !== null) {
      fields.sellingPrice = formatPrice(trade.unit * (1 + trade.markup / 100));
    }
  }
  return { fields, imageFile, imageUrl, imageLink, expiredLink, basePrice, archived, customSpecs };
}

// Tracker exports list every line of a project's documents, including billed
// hours, fees, taxes and pass-through expenses that aren't catalog products.
// They're offered, but left unticked.
const NON_PRODUCT_CATEGORIES = new Set([
  'labor',
  'labour',
  'time track',
  'expense',
  'expenses',
  'reimbursable',
  'reimbursables',
  'shipping',
  'freight',
  'delivery',
  'markup',
  'design fee',
  'design fees',
  'fee',
  'fees',
  'tax',
  'taxes',
  'tariff',
  'tariffs',
]);
const NON_PRODUCT_NAMES =
  /^(labor|reimbursables?|tariffs?|tariff surcharge|designer markup|design fees?|(vat|sales) tax|freight|shipping|delivery)$/i;

export const isNonProductLine = (fields) =>
  NON_PRODUCT_CATEGORIES.has(norm(fields.category)) || NON_PRODUCT_NAMES.test(fields.name.trim());

// Prices that disagree, or one row archived and the other not, mean two items
// rather than one item listed twice: a product library can hold the same name at
// two prices. A blank price never disagrees.
const isOtherItem = (a, b) =>
  a.archived !== b.archived ||
  ['price', 'sellingPrice'].some(
    (field) => a.fields[field] && b.fields[field] && a.fields[field] !== b.fields[field]
  );

// The same product shows up once per document line in tracker exports (the
// proposal line has the selling price, the PO line the cost). Rows with the
// same name, vendor, SKU and description become one product; blanks are filled
// from the later rows.
function combineRepeats(parsed) {
  const groups = new Map();
  const ordered = [];
  parsed.forEach((entry) => {
    const f = entry.fields;
    const key = [f.name, f.vendor, f.sku, f.description].map(norm).join('|');
    const candidates = groups.get(key) || [];
    const existing = candidates.find((group) => !isOtherItem(group, entry));
    if (!existing) {
      const copy = {
        ...entry,
        fields: { ...entry.fields },
        sourceRows: [...entry.sourceRows],
        customSpecs: [...entry.customSpecs],
      };
      groups.set(key, [...candidates, copy]);
      ordered.push(copy);
      return;
    }
    Object.keys(existing.fields).forEach((field) => {
      if (!existing.fields[field] && entry.fields[field])
        existing.fields[field] = entry.fields[field];
    });
    ['imageFile', 'imageUrl', 'imageLink', 'expiredLink', 'basePrice'].forEach((key) => {
      if (!existing[key] && entry[key]) existing[key] = entry[key];
    });
    if (!existing.customSpecs.length) existing.customSpecs = [...entry.customSpecs];
    existing.sourceRows.push(...entry.sourceRows);
  });
  return ordered;
}

export function buildReviewRows({
  dataRows,
  mapping,
  headers = [],
  itemType = 'product',
  sellPriceMode = 'file',
  vendors = [],
  categoryNames = [],
  salesCodeOptions = [],
  existingProducts = [],
  now = Date.now(),
}) {
  const mode = effectiveSellPriceMode(mapping, sellPriceMode);
  const scale = markupScale(dataRows, mapping.indexOf('markup'));
  const parsed = dataRows
    .map((row) => ({
      ...readRowFields(row, mapping, { sellPriceMode: mode, scale, headers, now }),
      sourceRows: [row.rowNumber],
    }))
    .filter((entry) => entry.fields.name);
  const missingName = dataRows.length - parsed.length;
  const combined = combineRepeats(parsed);

  const vendorByName = new Map(vendors.map((v) => [norm(v.name), v]));
  // New vendors and categories take the first spelling in the file, so
  // "Queen" and "queen" don't become two of them.
  const newVendorNames = new Map();
  const categoryByName = new Map(categoryNames.map((name) => [norm(name), name]));
  const salesCodeByKey = new Map();
  salesCodeOptions.forEach((option) => {
    salesCodeByKey.set(norm(option.salesCode), option.salesCode);
    salesCodeByKey.set(norm(option.name), option.salesCode);
  });
  const catalogKeys = catalogKeySet(existingProducts);

  let alreadyInCatalog = 0;
  let archived = 0;
  let nonProduct = 0;
  let expiredPhotos = 0;
  const rows = combined.map((entry, index) => {
    if (entry.expiredLink && !entry.imageLink) expiredPhotos += 1;
    const fields = { ...entry.fields };
    const vendor = vendorByName.get(norm(fields.vendor));
    let vendorChoice = '';
    if (vendor) {
      vendorChoice = vendor.vendorId;
    } else if (fields.vendor) {
      if (!newVendorNames.has(norm(fields.vendor))) {
        newVendorNames.set(norm(fields.vendor), fields.vendor);
      }
      vendorChoice = `new:${newVendorNames.get(norm(fields.vendor))}`;
    }
    if (fields.category) {
      if (!categoryByName.has(norm(fields.category))) {
        categoryByName.set(norm(fields.category), fields.category);
      }
      fields.category = categoryByName.get(norm(fields.category));
    }
    fields.salesCode = salesCodeByKey.get(norm(fields.salesCode)) || '';
    const inCatalog = catalogKeys.has(catalogKey(fields.name, vendor?.name || fields.vendor));
    // Labor or a fee is what a services import is for.
    const isNonProduct = itemType !== 'service' && isNonProductLine(fields);
    if (inCatalog) alreadyInCatalog += 1;
    else if (entry.archived) archived += 1;
    else if (isNonProduct) nonProduct += 1;
    return {
      id: `row-${entry.sourceRows[0]}-${index}`,
      sourceRows: entry.sourceRows,
      // Archived in the old system, or not a product: offered, but left out
      // unless ticked.
      selected: !inCatalog && !entry.archived && !isNonProduct,
      alreadyInCatalog: inCatalog,
      archived: entry.archived,
      nonProduct: isNonProduct,
      fields,
      vendorChoice,
      imageFile: entry.imageFile,
      imageUrl: entry.imageUrl,
      imageLink: entry.imageLink,
      basePrice: entry.basePrice,
      customSpecs: entry.customSpecs,
    };
  });

  return {
    rows,
    stats: {
      combinedRows: parsed.length - combined.length,
      missingName,
      alreadyInCatalog,
      archived,
      nonProduct,
      expiredPhotos,
    },
  };
}

/* ============================================================
   Checks before import
============================================================ */

export function getRowErrors(row) {
  const errors = {};
  if (!row.fields.name.trim()) errors.name = 'Required';
  const price = Number(row.fields.price);
  if (row.fields.price === '' || !Number.isFinite(price) || price <= 0) errors.price = 'Required';
  if (row.fields.sellingPrice !== '') {
    const sell = Number(row.fields.sellingPrice);
    if (!Number.isFinite(sell) || sell < 0) errors.sellingPrice = 'Enter a price';
  }
  return errors;
}

// Everything the review flags, in the order it's listed. Only the first three
// stop the import; the Add Product form doesn't require the rest either.
export const ISSUE_KEYS = [
  'name',
  'price',
  'sellingPrice',
  'belowTrade',
  'salesCode',
  'category',
  'vendor',
  'image',
];
export const BLOCKING_ISSUES = new Set(['name', 'price', 'sellingPrice']);

// What's still missing on one row. Services often have no vendor, so that
// isn't flagged for them. A selling price under the trade price usually means
// the file's quantities don't match, like fabric sold by the yard but bought
// on one line. A photo the file links to counts as an image.
export function rowIssues(row, itemType) {
  const errors = getRowErrors(row);
  const issues = ['name', 'price', 'sellingPrice'].filter((key) => errors[key]);
  if (
    !errors.price &&
    !errors.sellingPrice &&
    row.fields.sellingPrice !== '' &&
    Number(row.fields.sellingPrice) < Number(row.fields.price)
  ) {
    issues.push('belowTrade');
  }
  if (!row.fields.salesCode) issues.push('salesCode');
  if (!String(row.fields.category || '').trim()) issues.push('category');
  if (itemType !== 'service' && !row.vendorChoice) issues.push('vendor');
  if (!row.imageUrl && !row.imageLink) issues.push('image');
  return issues;
}

// Counts each issue across the selected rows.
export function summarizeRows(rows, itemType) {
  const selected = rows.filter((row) => row.selected);
  const issues = Object.fromEntries(ISSUE_KEYS.map((key) => [key, 0]));
  let fillableTradePrices = 0;
  selected.forEach((row) => {
    const found = rowIssues(row, itemType);
    found.forEach((key) => {
      issues[key] += 1;
    });
    if (found.includes('price') && row.basePrice) fillableTradePrices += 1;
  });
  return {
    selected: selected.length,
    issues,
    fillableTradePrices,
    blocking: issues.name + issues.price + issues.sellingPrice,
  };
}

// Fills missing trade prices on selected rows with their price before markup.
// Untouched rows keep their identity so the review table only redraws changes.
export function fillMissingTradePrices(rows) {
  let filled = 0;
  const next = rows.map((row) => {
    if (!row.selected || !row.basePrice || !getRowErrors(row).price) return row;
    filled += 1;
    return { ...row, fields: { ...row.fields, price: row.basePrice } };
  });
  return { rows: next, filled };
}

/* ============================================================
   Saving
============================================================ */

const optionalNumber = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

// Same shape the Add Product / Add Service forms send to POST /api/products.
export function buildProductPayload(row, { itemType, userId, vendorId = '', vendorName = '' }) {
  const f = row.fields;
  const payload = {
    userId,
    type: itemType === 'service' ? 'service' : 'product',
    name: f.name.trim(),
    salesCode: f.salesCode,
    category: f.category,
    vendorId,
    vendor: vendorName,
    price: Number(f.price),
    sellingPrice: optionalNumber(f.sellingPrice),
    link: f.link,
    description: f.description,
    notes: f.notes,
    internalNotes: f.notes,
    imageUrl: row.imageUrl || '',
  };
  if (itemType === 'service') return payload;
  return {
    ...payload,
    sku: f.sku,
    color: f.color,
    finishes: f.finishes,
    materials: f.materials,
    msrp: optionalNumber(f.msrp),
    shipping: f.shipping,
    tariff: f.tariff,
    length: f.length,
    width: f.width,
    height: f.height,
    depth: f.depth,
    customSpecs: (row.customSpecs || []).map(({ label, value }) => ({ label, value })),
  };
}

// Runs `worker` over `items` with at most `limit` running at once, keeping the
// results in the same order as the items.
export async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

/* ============================================================
   Image files
============================================================ */

// Exported filenames can be URL-encoded ("rgb_%25252..."), so compare the name
// as written and decoded, ignoring case and any folder path.
export function imageNameKeys(name) {
  const base = String(name || '')
    .trim()
    .split(/[\\/]/)
    .pop();
  const keys = new Set();
  let current = base;
  for (let i = 0; i < 3 && current; i += 1) {
    keys.add(current.toLowerCase());
    let decoded;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      break;
    }
    if (decoded === current) break;
    current = decoded;
  }
  return keys;
}

// Pairs uploaded files with the rows whose image filename they match.
// Returns [{ file, rowIds }] plus the files that matched nothing.
export function matchImageFiles(rows, files) {
  const matches = [];
  const unmatched = [];
  files.forEach((file) => {
    const fileKeys = imageNameKeys(file.name);
    const rowIds = rows
      .filter(
        (row) => row.imageFile && [...imageNameKeys(row.imageFile)].some((key) => fileKeys.has(key))
      )
      .map((row) => row.id);
    if (rowIds.length) matches.push({ file, rowIds });
    else unmatched.push(file);
  });
  return { matches, unmatched };
}
